import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../Axios/AxiosInstance';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Approval.css';
import Header from '../Components/Header/Header';
import Footer from '../Components/Footer/Footer';
import { TableTextConstants, TextConstants } from '../Constants/Constants';
import ReusableTable from '../Components/Table/ReusableTable';

const Approval = () => {
  const navigate = useNavigate();
  const [approvalData, setApprovalData] = useState([]);
  const [buttonLoading, setButtonLoading] = useState(false);


  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const pageDataRef = useRef({});

  useEffect(() => {
    fetchApprovalData();
  }, [currentPage, rowsPerPage]);


  const fetchApprovalData = async (force = false) => {
    const cacheKey = `${currentPage}-${rowsPerPage}`;
    if (!force && pageDataRef.current[cacheKey]) {
      setApprovalData(pageDataRef.current[cacheKey]);
    } else {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get('/Approval/PendingApporvalList', {
          params: {
            PageNumber: currentPage,
            PageSize: rowsPerPage,
          },
        });
  
        const { apporvalPendingList, paginatedData } = response.data;
  
        if (apporvalPendingList && Array.isArray(apporvalPendingList)) {
          const formattedData = apporvalPendingList.map((item) => ({
            createdAt: new Date(item.createdAt).toLocaleDateString('en-GB'),
            transactionId: item.transactionId,
            totalAmount: item.totalAmount,
            dealerId: item.dealerId,
            painterId: item.painterId,
            totalHandlingCharges: item.totalHandlingCharges,
            totalFinalAmount: item.totalFinalamount,
          }));
  
          // Persist selected rows
          const updatedSelectedRows = formattedData.filter((row) =>
            selectedRows.some((selected) => selected.transactionId === row.transactionId)
          );
  
          setSelectedRows(updatedSelectedRows);
          setApprovalData(formattedData);
          setTotalRecords(paginatedData.totalRecords);
          pageDataRef.current[cacheKey] = formattedData;
        } else {
          console.error('Data format is incorrect or missing:', apporvalPendingList);
        }
      } catch (error) {
        console.error('Error fetching approval data:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };


  const handleApprove = () => {
    if (selectedRows.length === 0) {
      toast.error('Please choose at least one option to approve.');
      return;
    }

    const selectedDates = selectedRows.map(row => row.createdAt.split('/').reverse().join('-'));
    sendPendingApproval(selectedDates);
  };

  const sendPendingApproval = (selectedDates) => {
    setButtonLoading(true);
    axiosInstance.post('/Approval/SendPendingApporval', 
      { selectedDate: selectedDates },
      { timeout: 60000 } 
    )
      .then(response => {
        const allItems = response.data.flatMap(entry => entry.items);
  
        const hasFailure = allItems.some(item => item.status === 'FAILURE');
  
        if (hasFailure) {
          toast.error('Dealer Approval Not Sent Successfully.');
        } else {
          toast.success('Approved Successfully');
        }
  
        setButtonLoading(false);
        fetchApprovalData(true);
      })
      .catch(error => {
        console.error("Error in API:", error);
        toast.error('Dealer Approval Not Sent Successfully.');
        fetchApprovalData(true);
        setButtonLoading(false);
      });
  };
  

  const columns = [
    { key: 'createdAt', label: 'Date' },
    { key: 'totalAmount', label: 'Amount', isTextAlignRight: true, isCurrency: true},
    { key: 'totalHandlingCharges', label: 'Handling Fee', isTextAlignRight: true, isCurrency: true },
    { key: 'totalFinalAmount', label: 'Total Amount', isTextAlignRight: true, isCurrency: true },
  ];

  const handleSelectedRowsChange = (newSelectedRows) => {
    setSelectedRows(newSelectedRows);
  };


  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = Number(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };


  const handleEyeClick = (row) => {
    const { createdAt} = row;
    const formattedDate = createdAt.split('/').reverse().join('-');
    navigate(`/dealer-list/${formattedDate}`);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header title={"Pending Approval"} />
      <div className="flex flex-col flex-1 mx-2 my-4" >
        <div className="mt-3 p-1">
          <ReusableTable
            data={approvalData}
            columns={columns}
            rowsPerPage={rowsPerPage}
            loading={isLoading}
            currentPage={currentPage}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSelectedRowsChange={handleSelectedRowsChange}
            enableCheckbox={true}
            enableEye={true}
            onEyeClick={handleEyeClick}
          />
        </div>

        {approvalData.length > 0 && (
          <div className="approval-button-container">
            <button className={`approve-button ${buttonLoading ? 'loading' : ''}`} onClick={handleApprove} disabled={buttonLoading}>
              <span className="button-text">{TextConstants.approve}</span>
              <span className="loader"></span>
            </button>
          </div>
        )}

      </div>
      <Footer />
      <ToastContainer />
    </div>

  );
};

export default Approval;
